/** 将语音控制器的外部状态接到 Preact，并确保每个聊天根只有一个连接。 */

import { useEffect, useRef, useState } from "preact/hooks";

import type { Conversation } from "../conversation/conversation";
import type { VoiceMode } from "../config";
import {
  createVoiceController,
  type VoiceController,
  type VoiceSnapshot,
} from "./controller";

const OFF_SNAPSHOT: VoiceSnapshot = {
  enabled: false,
  status: "off",
};

export interface VoiceViewState extends VoiceSnapshot {
  onToggle: () => void;
}

export function useVoiceController(
  url: string | undefined,
  voiceMode: VoiceMode | undefined,
  conversation: Conversation,
): VoiceViewState {
  const controller = useRef<VoiceController>();
  const [, redraw] = useState(0);
  useEffect(() => {
    controller.current?.dispose();
    controller.current = undefined;
    if (url === undefined) {
      redraw((value) => value + 1);
      return undefined;
    }
    const next = createVoiceController(url, voiceMode, conversation);
    controller.current = next;
    const unsubscribe = next.subscribe(() => {
      redraw((value) => value + 1);
    });
    redraw((value) => value + 1);
    return () => {
      unsubscribe();
      next.dispose();
      if (controller.current === next) controller.current = undefined;
    };
  }, [url, voiceMode, conversation]);

  const current = controller.current?.getSnapshot() ?? OFF_SNAPSHOT;
  return {
    ...current,
    onToggle: () => {
      controller.current?.toggle();
    },
  };
}
